import { FormEvent, useState } from "react";
import api, { errorStatus } from "../../api/account";
import { mapResponse } from "../../auth/mapUser";
import { AuthUser } from "../../auth/types";
import PinInput from "./PinInput";

const PIN_LENGTH = 5;

type SignInFormProps = {
  onSignIn: (user: AuthUser) => void;
};

export default function SignInForm({ onSignIn }: SignInFormProps) {
  const [uid, setUid] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const ready = uid.trim().length > 0 && pin.length === PIN_LENGTH;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ready || loading) return;

    setError("");
    setLoading(true);

    try {
      const data = await api.account.getMetadata(uid.trim(), pin.trim());

      if (!data.User) {
        setError("Invalid response from server. Please try again.");
        setLoading(false);
        return;
      }

      onSignIn(mapResponse(data, uid.trim(), pin.trim()));
    } catch (err) {
      const status = errorStatus(err);
      setError(
        status === 401 || status === 403
          ? "Sign-in failed. Please check your credentials."
          : `Sign-in failed (${
              status ?? "network error"
            }). Please check your connection.`
      );
      setPin("");
    }

    setLoading(false);
  };

  return (
    <div className="account-page account-page--centered">
      <div className="signin-card">
        <div className="signin-brand">
          <img
            src="https://plnotimages.z29.web.core.windows.net/pliconround.png"
            alt="Pocket Ledger"
          />
        </div>

        <h1 className="signin-title">Welcome back</h1>
        <p className="signin-subtitle">
          Sign in to manage your subscription, devices and support requests.
        </p>

        <form className="signin-form" onSubmit={handleSubmit}>
          <div className="signin-field">
            <label htmlFor="signin-uid">User ID</label>
            <input
              id="signin-uid"
              type="email"
              placeholder="you@example.com"
              value={uid}
              onChange={(e) => setUid(e.target.value)}
              autoComplete="username"
              required
              autoFocus
            />
          </div>

          <div className="signin-field">
            <label>PIN</label>
            <PinInput
              value={pin}
              onChange={setPin}
              length={PIN_LENGTH}
              disabled={loading}
            />
          </div>

          {error && <div className="signin-error">{error}</div>}

          <button className="signin-btn" type="submit" disabled={!ready || loading}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <p className="signin-hint">
          Use the same email and PIN you use in the Pocket Ledger app.
        </p>
      </div>
    </div>
  );
}
